import { Input } from "@material-tailwind/react";
import { t } from "i18next";

interface SignatorySelectorProps {
  contacts: EasyTech.Contact[];
  manualMode: boolean;
  setManualMode: (b: boolean) => void;
  manualFields: { firstname: string; lastname: string; function: string };
  setManualFields: (f: { firstname: string; lastname: string; function: string }) => void;
  selectedContact: EasyTech.Contact | null;
  setSelectedContact: (c: EasyTech.Contact | null) => void;
  canEdit: boolean;
  workorder: any;
  isOnsiteSelected: boolean;
  setIsOnsiteSelected: (b: boolean) => void;
  initialOnsiteFields: { firstname: string; lastname: string; function: string };
}

export default function SignatorySelector({
  contacts,
  manualMode,
  setManualMode,
  manualFields,
  setManualFields,
  selectedContact,
  setSelectedContact,
  canEdit,
  workorder,
  isOnsiteSelected,
  setIsOnsiteSelected,
  initialOnsiteFields
}: SignatorySelectorProps) {
  // Vérifier si les champs ont été modifiés par rapport aux valeurs initiales du contact onsite
  const areOnsiteFieldsModified = () => {
    return (
      manualFields.firstname !== initialOnsiteFields.firstname ||
      manualFields.lastname !== initialOnsiteFields.lastname ||
      manualFields.function !== initialOnsiteFields.function
    );
  };

  return (
    <div className="mb-4">
      <label className="text-gray-700 mb-2 block">{t("Contact")}</label>
      <select
        className="form-field w-full h-10 mb-3"
        value={manualMode ? "manual" : (isOnsiteSelected ? "onsite" : (selectedContact?.id ?? ""))}
        onChange={e => {
          if (e.target.value === "manual") {
            setManualMode(true);
            setSelectedContact(null);
            setIsOnsiteSelected(false);
          } else if (e.target.value === "onsite") {
            setManualMode(false);
            setSelectedContact(null);
            setIsOnsiteSelected(true);
            // Pré-remplir les champs avec les informations du contact onsite
            if (workorder?.onSiteContact?.name) {
              const nameParts = workorder.onSiteContact.name.split(" ");
              setManualFields({
                firstname: nameParts[0] || "",
                lastname: nameParts.slice(1).join(" ") || "",
                function: ""
              });
            }
          } else {
            setManualMode(false);
            setIsOnsiteSelected(false);
            setSelectedContact(contacts.find(c => c.id === e.target.value) || null);
          }
        }}
        disabled={!canEdit}
      >
        <option value="" disabled>
          {t("common:selectContact")}
        </option>
        {contacts.map((contact) => (
          <option key={contact.id} value={contact.id}>
            {contact.firstName} {contact.lastName}
          </option>
        ))}
        {workorder?.onSiteContact?.name && (
          <option value="onsite">
            {t("common:contactOnSite")}: {workorder.onSiteContact.name}
          </option>
        )}
        <option value="manual">{t("common:orAddSignatoryDetails")}</option>
      </select>
      
      {/* Afficher les champs d'édition pour le mode manuel ou si le contact onsite est sélectionné */}
      {(manualMode || isOnsiteSelected) && (
        <div className="grid grid-cols-2 gap-4">
          <Input
            className="form-field bg-white"
            type="text"
            color="blue"
            label={t("First name")}
            placeholder={t("First name")}
            value={manualFields.firstname}
            onChange={e => canEdit && setManualFields({ ...manualFields, firstname: e.target.value })}
            readOnly={!canEdit}
          />
          <Input
            className="form-field bg-white"
            type="text"
            color="blue"
            label={t("Last name")}
            placeholder={t("Last name")}
            value={manualFields.lastname}
            onChange={e => canEdit && setManualFields({ ...manualFields, lastname: e.target.value })}
            readOnly={!canEdit}
          />
          <Input
            className="form-field col-span-2 bg-white"
            type="text"
            color="blue"
            label={t("Function")}
            placeholder={t("Function")}
            value={manualFields.function}
            onChange={e => canEdit && setManualFields({ ...manualFields, function: e.target.value })}
            readOnly={!canEdit}
          />
          
          {/* Afficher un indicateur si les champs ont été modifiés par rapport au contact onsite initial */}
          {isOnsiteSelected && areOnsiteFieldsModified() && (
            <div className="col-span-2 text-blue-500 text-sm">
              {t("On Site Contact information will be updated")}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 